import { Link } from '@tanstack/react-router'
import { TopBar as RootsTopBar } from '@trellis/roots/navigation'
import type { BreadcrumbItem } from '@trellis/roots/navigation'
import { EnvBadge } from '@trellis/roots/status'
import { Search } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'

import { NotificationBell } from '@/components/notifications'
import { CommandPalette } from '@/components/shell/CommandPalette'
import { env } from '@/env'
import { useOrganization } from '@/hooks/useOrganization'
import { BreadcrumbsContext } from '@/lib/breadcrumbs'

interface TopBarProps {
  onOpenMenu: () => void
  isHidden?: boolean
}

export function TopBar({ onOpenMenu, isHidden }: TopBarProps) {
  const { breadcrumbs } = useContext(BreadcrumbsContext)
  const { data: orgResult } = useOrganization()
  const orgName = orgResult?.data?.name ?? 'myTrellis'
  const [commandOpen, setCommandOpen] = useState(false)

  const allCrumbs: BreadcrumbItem[] = [{ label: orgName, href: '/' }, ...breadcrumbs]

  useEffect(() => {
    function handleKeyDown(keyEvent: KeyboardEvent) {
      if (keyEvent.key === 'k' && (keyEvent.metaKey || keyEvent.ctrlKey)) {
        keyEvent.preventDefault()
        setCommandOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <RootsTopBar
        onOpenMenu={onOpenMenu}
        isHidden={isHidden}
        breadcrumbs={allCrumbs}
        linkComponent={Link}
        envBadge={<EnvBadge env={env.VITE_DEPLOY_ENV} />}
        search={
          <button
            type="button"
            aria-label="Search"
            onClick={() => setCommandOpen(true)}
            className="bg-muted/50 text-muted-foreground hover:bg-muted flex h-[34px] min-w-0 flex-1 items-center gap-2 rounded-full border px-3 text-sm transition-colors md:w-72 md:flex-none"
          >
            <Search className="size-3.5 shrink-0" />
            <span className="flex-1 truncate text-left">Search…</span>
            <kbd className="text-muted-foreground/60 hidden font-mono text-[11px] sm:inline">
              ⌘K
            </kbd>
          </button>
        }
        actions={<NotificationBell />}
      />

      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
    </>
  )
}
